import React, { useState } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  InputAdornment,
  FormHelperText,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import LockIcon from "@mui/icons-material/Lock";
import apiService from "../../services/apiService";
import { CREDENTIAL_SESSION, TOKEN_NAME } from "../../helper/constants";
import { dispatchNotification, setCookie } from "../../helper/utils";
import "./styles.scss";

/**
 * Credential login form, shared by the two non-SSO entry points:
 *
 *   mode="ios"      ?oneapplogin=true + IOS_LOGIN_FEATURE flag → POST /auth/login
 *   mode="internal" ?internallogin=true                       → POST /internal/session/create
 *
 * Both end the same way: mark the session so the router's SSO gate validates
 * with /user instead of redirecting to SAML, then hard-reload into the app,
 * and both identify the user by employeeId.
 */
const ID_LABEL = "employee ID";

const SUBMIT_BY_MODE = {
  ios: (employeeId, password) => apiService.login({ employeeId, password }),
  internal: (employeeId, password) =>
    apiService.createInternalSession({ employeeId, password }),
};

const LoginPage = ({ mode = "ios" }) => {
  const submit = SUBMIT_BY_MODE[mode] || SUBMIT_BY_MODE.ios;
  const [employeeId, setEmployeeId] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setPassword(value);
    setLoginError("");
  };

  const handleEmployeeIdChange = (e) => {
    setEmployeeId(e.target.value);
    setLoginError("");
  };

  const handleLogin = async () => {
    if (!employeeId.trim()) {
      setLoginError(`Please enter your ${ID_LABEL}`);
      return;
    }

    setIsLoading(true);
    setLoginError("");

    try {
      const response = await submit(employeeId.trim(), password);

      // Parse response if it's a string
      const responseData = typeof response.data === 'string'
        ? JSON.parse(response.data)
        : response.data;

      // Store token if provided
      if (responseData?.token || responseData?.data?.token) {
        const token = responseData.token || responseData.data.token;
        setCookie(TOKEN_NAME, token, 30);
      }

      // Store user details in sessionStorage
      let userDetails = null;
      if (responseData?.user || responseData?.data?.user) {
        userDetails = responseData.user || responseData.data.user;
        sessionStorage.setItem("user_details", JSON.stringify(userDetails));
      } else if (responseData?.employeeId || responseData?.data?.employeeId) {
        // Store basic user info if full user object not available
        userDetails = {
          employeeId: responseData.employeeId || responseData.data?.employeeId,
        };
        sessionStorage.setItem("user_details", JSON.stringify(userDetails));
      }

      // Tell the router's SSO gate that this tab is authenticated. It would
      // otherwise find no session on the reload below and bounce to SAML,
      // throwing away the login that just succeeded.
      sessionStorage.setItem(CREDENTIAL_SESSION, "true");
      // Mark that user just logged in - actual event will be tracked in router with full data
      sessionStorage.setItem("login_pending", "true");

      dispatchNotification({
        type: "success",
        message: "Login successful!",
        timeout: 3000,
      });

      // Full document load rather than navigate() + reload(): it drops the
      // oneapplogin param, remounts the router so the gate re-evaluates with
      // the session cookie in place, and leaves no half-torn-down state.
      setTimeout(() => {
        window.location.href = "/oneapp/home";
      }, 500);
    } catch (error) {
      setIsLoading(false);
      // request.js's onError rejects with a plain string (the backend's
      // data.errorMessage, else "Something Went Wrong") — never an axios error,
      // so there is no error.response to inspect here.
      const errorMessage =
        (typeof error === "string" && error) || "Invalid credentials";

      setLoginError(errorMessage);
      dispatchNotification({
        type: "error",
        message: errorMessage,
        timeout: 4000,
      });
    }
  };

  return (
    <Box className="login-page">
      {/* Header with Jio Logo */}
      <Box className="login-header">
        <Box className="jio-logo">
          <Typography className="jio-logo-text">Jio</Typography>
        </Box>
      </Box>

      {/* Main Content */}
      <Box className="login-content">
        <Typography className="welcome-title">Welcome to Reliance Jio</Typography>
        <Typography className="signin-subtitle">Sign in with Jio account</Typography>

        {/* Invalid Credentials Error Message */}
        {loginError && (
          <Typography className="invalid-credentials-error">
            {loginError}
          </Typography>
        )}

        {/* Login Form */}
        <Box className="login-form">
          <TextField
            fullWidth
            placeholder={ID_LABEL}
            value={employeeId}
            onChange={handleEmployeeIdChange}
            className="login-input"
            error={!!loginError}
            disabled={isLoading}
            variant="standard"
            InputProps={{
              disableUnderline: false,
              endAdornment: (
                <InputAdornment position="end">
                  <PersonIcon className="input-icon" />
                </InputAdornment>
              ),
            }}
          />

          <Box>
            <TextField
              fullWidth
              type="password"
              placeholder="password"
              value={password}
              onChange={handlePasswordChange}
              className="login-input"
              error={!!loginError}
              disabled={isLoading}
              variant="standard"
              InputProps={{
                disableUnderline: false,
                endAdornment: (
                  <InputAdornment position="end">
                    <LockIcon className="input-icon" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <Button
            fullWidth
            variant="contained"
            className="login-button"
            onClick={handleLogin}
            disabled={!employeeId || !password || isLoading}
          >
            {isLoading ? "Logging in..." : "Login"}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default LoginPage;